'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { HideSiteChrome } from '@/components/HideSiteChrome';
import { useAnalytics } from '@/hooks/useAnalytics';

// three.js is ~600KB and not-found.tsx is the global 404 boundary, so a static
// import would leak it into the shared bundle loaded on every route. Lazy and
// client-only, so it ships only when a 404 actually renders.
const AnimatedShaderBackground = dynamic(() => import('@/components/ui/animated-shader-background').then(m => m.AnimatedShaderBackground), {
  ssr: false
});

// Fun 404 messages that change on refresh
const notFoundMessages = ['404 - Looks like this page is still in 0-to-1 development. Let me take you back to something functional.', "Page not found. Even my ML algorithms couldn't predict where this one went.", "This page exists only in a parallel codebase. Let's get you back to this reality.", "404 - I've built tools to find almost anything, but this page is beyond my reach.", "You've discovered a rare bug in the matrix. Let's trace our steps back to the working code.", 'Page missing. Must be off implementing a feature that seemed simple but turned into a 3AM coding session.', "404 - You've wandered into uncharted territory. I'm usually building the map, not getting lost in it.", "This route doesn't exist. Let's refactor your journey back to the main branch.", "Oops! Wrong endpoint. Let's get you back to where the API actually responds.", "404 - This path returned undefined. Let's try a function that actually exists."];
export default function NotFound() {
  const $ = _c(12);
  const [message, setMessage] = useState(notFoundMessages[0]);
  const {
    track
  } = useAnalytics();
  let t0;
  let t1;
  if ($[0] !== track) {
    t0 = () => {
      setMessage(notFoundMessages[Math.floor(Math.random() * notFoundMessages.length)]);
      track("page_not_found", {
        path: window.location.pathname + window.location.search,
        referrer: document.referrer || undefined
      });
    };
    t1 = [track];
    $[0] = track;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <HideSiteChrome />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      zIndex: 50
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="absolute inset-0"><AnimatedShaderBackground /></div>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <h1 className="text-6xl font-semibold text-white sm:text-8xl">404</h1>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = message || "Sorry, we couldn't find the page you're looking for.";
  let t7;
  if ($[7] !== t6) {
    t7 = <p className="mt-6 max-w-md text-base leading-relaxed text-zinc-300/90">{t6}</p>;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Link href="/" className="ease-out-strong mt-8 inline-flex items-center justify-center bg-transparent px-8 py-3 font-mono text-sm text-white transition-colors duration-200 hover:bg-white hover:text-black focus:outline-none">{"<< GO_BACK_HOME >>"}</Link>;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t7) {
    t9 = <>{t2}<div className="fixed inset-0 size-full overflow-hidden bg-neutral-950" style={t3}>{t4}<div className="relative flex size-full items-center justify-center px-6"><div className="flex flex-col items-center text-center font-mono">{t5}{t7}{t8}</div></div></div></>;
    $[10] = t7;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  return t9;
}